/* eslint-disable @typescript-eslint/no-use-before-define */
import { type ReactNode } from 'react';
import * as React from 'react';
import { motion } from 'framer-motion';
import styled, { css } from 'styled-components';

import {
  AirflowLogo,
  AthenaLogo,
  ClickhouseLogo,
  DagsterLogo,
  DbtLogo,
  DremioLogo,
  GitHubLogo,
  GitLabLogo,
  MariaDBLogo,
  MSSQLServerLogo,
  MySQLLogo,
  PostgresLogo,
  PrefectLogo,
  SnowflakeLogo,
  SparkLogo,
  TrinoLogo
} from '../../icons/IntegrationLogos';
import { ReactComponent as APIIlly } from '../../images/illustrations/illustration-api.svg';
import { ReactComponent as DbtIlly } from '../../images/illustrations/illustration-dbt.svg';
import { ReactComponent as GitFlowIlly } from '../../images/illustrations/illustration-git-flow.svg';
import { ReactComponent as OrchestrationIlly } from '../../images/illustrations/illustration-orchestration.svg';
import { ReactComponent as PushPythonIlly } from '../../images/illustrations/illustration-push-python.svg';
import { ReactComponent as MiniTronIlly } from '../../images/illustrations/illustration-tron-mini.svg';
import { ReactComponent as BigQueryLogoFull } from '../../images/logos/BigQuery.svg';
import { ReactComponent as DatabricksLogoFull } from '../../images/logos/Databricks.svg';
import { ReactComponent as RedshiftLogoFull } from '../../images/logos/Redshift.svg';
import { ReactComponent as SnowflakeLogoFull } from '../../images/logos/snowflake.svg';
import { ANIMATIONS, DOCS_LINKS } from '../../utils';
import { Button, GradientHeader, Heading, Text } from '../design-system';

import { GridItem, type GridItemType } from './GridItem';

const Wrapper = styled(motion.div)`
  display: flex;
  flex-direction: column;
  width: 100%;
  padding: 70px 0 60px;
  position: relative;
`;

const Inner = styled.div`
  display: flex;
  flex-direction: column;
  max-width: ${({ theme }) => theme.maxWidth.PAGE};
  padding: 40px;
  margin: 0 auto;
  z-index: 1;
  position: relative;
  width: 100%;
`;

const Top = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  gap: 15px;
  margin-bottom: 70px;
  position: relative;

  @media (max-width: ${({ theme }) => theme.mediaQuery.MEDIUM}) {
    margin-bottom: 30px;
  }
`;

const StyledHeading = styled(GradientHeader).attrs({
  $purple: true
})`
  && {
    margin-bottom: 0;
    max-width: 800px;
  }
`;

const Description = styled(Text).attrs({ size: 'extra-large' })`
  text-align: center;
  max-width: 500px;
  margin-bottom: 0;
`;

const Grid = styled.div`
  display: grid;
  grid-template-columns: 1fr 1fr 1fr;
  gap: 20px;

  @media (max-width: ${({ theme }) => theme.mediaQuery.LARGE}) {
    grid-template-columns: minmax(0, 1fr) minmax(0, 1fr);
  }

  @media (max-width: ${({ theme }) => theme.mediaQuery.SMALL}) {
    display: flex;
    flex-direction: column;
  }
`;

const Illustration = styled.div`
  display: flex;
  justify-content: center;
  width: 100%;
  height: 160px;
  flex: none;
  overflow: hidden;
  transition: transform ${({ theme }) => theme.animation.DURATION}
    ${({ theme }) => theme.animation.EASING};
`;

const Info = styled.div`
  padding: 22px 20px 10px;
  transition: transform ${({ theme }) => theme.animation.DURATION}
    ${({ theme }) => theme.animation.EASING};
`;

const StyledGridItem = styled(GridItem)`
  &&& {
    padding: 5px;

    &:hover {
      ${Illustration} {
        transform: scale(1.04) translateY(-5px);
      }

      ${Info} {
        transform: translateY(-3px);
      }
    }
  }
`;

const WarehouseLogos = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  position: relative;
  width: 100%;

  > svg {
    position: absolute;
    bottom: 0;
    left: 0;
    right: 0;
    z-index: 0;
    margin: 0 auto;
  }
`;

const MainWarehouseLogos = styled.div`
  display: flex;
  flex-direction: column;
  flex: none;
  gap: 10px;
  margin-bottom: 8px;
`;

const LogoRow = styled.div`
  display: flex;
  flex-wrap: wrap;
  align-items: center;
  justify-content: center;
  gap: 10px;

  > a {
    display: flex;
    position: relative;
    z-index: 1;

    svg {
      width: auto;
    }
  }
`;

const WarehouseLogoGrid = styled.div`
  display: flex;
  flex: none;
  gap: 3px;
  align-items: center;
  justify-content: center;

  > a {
    position: relative;
    z-index: 1;
  }
`;

const LogoIllustrationCombo = styled.div`
  position: relative;
  width: 100%;
  height: 100%;
  display: flex;
  align-items: flex-start;
  justify-content: center;
  padding-top: 25px;

  > svg {
    position: absolute;
    bottom: 0;
    left: 0;
    right: 0;
    z-index: 0;
    margin: 0 auto;

    [data-color='background'] {
      fill: ${({ theme }) => theme.backgroundColor};
    }
  }
`;

const LogoPairing = styled.div`
  display: flex;
  align-items: center;
  gap: 2px;
  position: relative;
  z-index: 1;
  width: 80%;
  flex-wrap: wrap;
  justify-content: center;

  @media (max-width: ${({ theme }) => theme.mediaQuery.MEDIUM}) {
    width: 100%;
  }
`;

const IntegButton = styled(Button)<{ $iconOnly?: boolean }>`
  &&& {
    padding: 0;

    ${({ $iconOnly }) =>
      $iconOnly &&
      css`
        width: 26px;
        flex: none;
      `};
  }
`;

const IntegLogo = styled.div`
  display: flex;
`;

const IntegText = styled(Text).attrs({ size: 'medium' })`
  color: ${({ theme }) => theme.colors.WHITE};
`;

const IntegWrapper = styled.div<{ $iconOnly?: boolean }>`
  display: flex;
  align-items: center;
  gap: 7px;
  padding: 0 8px;

  ${IntegLogo} {
    > div {
      width: 30px;
      height: 30px;
    }
  }

  ${({ $iconOnly }) =>
    $iconOnly &&
    css`
      padding: 0;

      ${IntegLogo} {
        > div,
        > div > svg {
          width: 20px;
          height: 20px;
        }
      }
    `};
`;

interface IntegProps {
  iconOnly?: boolean;
  logo: ReactNode;
  name?: string;
  slug?: string;
}

export const Integ: React.ComponentType<IntegProps> = React.memo(
  ({ logo, name, slug, iconOnly = false }) => {
    return (
      <IntegButton
        $iconOnly={iconOnly}
        disabled={!slug}
        link={slug ? `/product/integrations/${slug}` : undefined}
        size="small"
        stylez="minimal"
        text={
          <IntegWrapper $iconOnly={iconOnly}>
            <IntegLogo>{logo}</IntegLogo>
            {name && <IntegText>{name}</IntegText>}
          </IntegWrapper>
        }
      />
    );
  }
);

const INTEGRATION_ITEMS: GridItemType[] = [
  {
    title: 'Built for the warehouse',
    illustration: (
      <WarehouseLogos>
        <MainWarehouseLogos>
          <LogoRow>
            <Integ logo={<SnowflakeLogoFull />} slug="snowflake" />
            <Integ logo={<RedshiftLogoFull />} slug="redshift" />
          </LogoRow>
          <LogoRow>
            <Integ logo={<BigQueryLogoFull />} slug="bigquery" />
            <Integ logo={<DatabricksLogoFull />} slug="databricks" />
          </LogoRow>
        </MainWarehouseLogos>
        <WarehouseLogoGrid>
          <Integ iconOnly={true} logo={<PostgresLogo />} slug="postgres" />
          <Integ iconOnly={true} logo={<ClickhouseLogo />} slug="clickhouse" />
          <Integ iconOnly={true} logo={<MariaDBLogo />} slug="mariadb" />
          <Integ iconOnly={true} logo={<AthenaLogo />} slug="athena" />
          <Integ
            iconOnly={true}
            logo={<MSSQLServerLogo />}
            slug="ms-sql-server"
          />
          <Integ iconOnly={true} logo={<MySQLLogo />} slug="mysql" />
          <Integ iconOnly={true} logo={<TrinoLogo />} slug="trino" />
          <Integ iconOnly={true} logo={<DremioLogo />} slug="dremio" />
        </WarehouseLogoGrid>
        <MiniTronIlly />
      </WarehouseLogos>
    ),
    link: DOCS_LINKS.DataConnections,
    description:
      'Hex has built-in connections to the most popular data warehouses, lakehouses, and databases. It’s easy to set up data connections, and securely share them with your team.'
  },
  {
    title: 'dbt metadata, docs, & metrics',
    illustration: (
      <LogoIllustrationCombo>
        <LogoPairing>
          <Integ logo={<DbtLogo />} name="dbt" slug="dbt" />
        </LogoPairing>
        <DbtIlly />
      </LogoIllustrationCombo>
    ),
    link: DOCS_LINKS.dbt,
    description:
      'Hex has a deep integration with dbt, including automatically enriching schemas with dbt docs.'
  },
  {
    title: 'Push your Python',
    illustration: (
      <LogoIllustrationCombo>
        <LogoPairing>
          <Integ logo={<SnowflakeLogo />} name="Snowpark" slug="snowflake" />
          <Integ logo={<SparkLogo />} name="Spark" />
        </LogoPairing>
        <PushPythonIlly />
      </LogoIllustrationCombo>
    ),
    link: DOCS_LINKS.Snowpark,
    description:
      'Hex has deep integrations with Snowpark for Snowflake and Spark (soon), allowing you to push Python code to remote execution environments.'
  },
  {
    title: 'Git it together',
    illustration: (
      <LogoIllustrationCombo>
        <LogoPairing>
          <Integ logo={<GitHubLogo />} name="GitHub" slug="github" />
          <Integ logo={<GitLabLogo />} name="GitLab" />
        </LogoPairing>
        <GitFlowIlly />
      </LogoIllustrationCombo>
    ),
    link: DOCS_LINKS.GitExport,
    description:
      'Export your projects to GitHub or GitLab to create an audit trail.'
  },
  {
    title: 'Orchestration',
    illustration: (
      <LogoIllustrationCombo>
        <LogoPairing>
          <Integ logo={<AirflowLogo />} name="Airflow" slug="airflow" />
          <Integ logo={<DagsterLogo />} name="Dagster" slug="dagster" />
          <Integ logo={<PrefectLogo />} name="Prefect" slug="prefect" />
        </LogoPairing>
        <OrchestrationIlly />
      </LogoIllustrationCombo>
    ),
    link: DOCS_LINKS.API,
    description:
      'You can include Hex projects in DAGs in Airflow, Dagster, or Prefect.'
  },
  {
    title: 'Hex API',
    illustration: <APIIlly />,
    link: DOCS_LINKS.API,
    description:
      "Want to connect to something we haven't thought of? Use our powerful public API to write your own integrations."
  }
];

export const IntegrationsTout: React.ComponentType = React.memo(() => {
  return (
    <Wrapper {...ANIMATIONS.SimpleFadeIn}>
      <Inner>
        <Top>
          <StyledHeading renderAs="h1">
            Instant integration with the whole stack
          </StyledHeading>
          <Description>
            Out-of-the-box connections and flexible APIs make setup a breeze.
          </Description>
        </Top>
        <Grid>
          {INTEGRATION_ITEMS.map((item, index) => (
            <StyledGridItem
              key={index}
              DANGEROUS_AS_DIV
              isExternal={true}
              showBkgGradient={item.showBkgGradient}
              to={item.link}>
              <>
                <Illustration>{item.illustration}</Illustration>
                <Info>
                  <Heading
                    css={`
                      display: flex;
                      margin-bottom: 7px;
                    `}
                    renderAs="h4">
                    {item.title}
                  </Heading>
                  <div>{item.description}</div>
                </Info>
              </>
            </StyledGridItem>
          ))}
        </Grid>
      </Inner>
    </Wrapper>
  );
});
